import { Box } from '@vp/swan';
import React, { useCallback, useContext, useEffect, useRef } from 'react';
import ConversationEvent from '../../../components/ConversationEvent/ConversationEvent';
import MessageComponent from '../../MessageComponent/MessageComponent';
import TypingIndicator from '../../../components/TypingIndicator/TypingIndicator';
import { IMessage } from '../../../interfaces/component-interfaces';
import { shouldShowAdditionalDetails } from '../../../utils/component-utils';
import { CAContext } from '../../../contexts/CAContext';
import './ChatWindowContent.styles.scss';
import { useScrollLockInsideContainer } from '../../../hooks/custom-hooks/useScrollLockInsideContainer';

export interface IChatWindowContentProps {
  conversationMessages: IMessage[];
}

const ChatWindowContent: React.FC<IChatWindowContentProps> = ({ conversationMessages }) => {
  const { setChatState } = useContext(CAContext);

  const scrollToBottomOfChat = useCallback((elementId?: string) => {
    setTimeout(() => {
      if (typeof document === 'undefined') return;
      let chatBox: HTMLElement | null = null;
      chatBox = document.getElementById('chatWindow');

      if (chatBox) {
        // If elementId is provided, scroll to that specific element
        if (elementId) {
          const targetElement = document.getElementById(elementId);
          if (targetElement) {
            targetElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center', // Center the element in the view
              inline: 'nearest',
            });
            return;
          }
        }
        chatBox.scrollTo({
          top: chatBox.scrollHeight + 16,
          behavior: 'smooth',
        });
      }
    }, 100);
  }, []);

  useEffect(() => {
    setChatState((prev) => ({ ...prev, scrollToBottom: scrollToBottomOfChat }));
  }, [scrollToBottomOfChat]);

  useEffect(() => {
    scrollToBottomOfChat();
  }, [conversationMessages, scrollToBottomOfChat]);
  const chatRef = useRef<HTMLDivElement>(null);
  useScrollLockInsideContainer(chatRef);
  return (
    <Box className={`chatWindowBodyContainer`} id="chatWindow" ref={chatRef}>
      {conversationMessages?.map((item, index) => {
        const isLastMessage = shouldShowAdditionalDetails(conversationMessages, index);
        if (item.type === 'Typing') {
          if (typeof item.messageContent === 'string') {
            return (
              <MessageComponent
                id={item.id}
                key={item.id}
                messageContent={<TypingIndicator senderType="agent" key={item.id} />}
                messageType="agent"
                isLastMessage
                additionalComponents={item.additionalComponents}
                isMessageRead={item.isMessageRead}
                attachments={item.attachments}
                formatType={item.formatType}
              />
            );
          } else {
            return item.messageContent;
          }
        }
        if (item.type === 'system') {
          return (
            <ConversationEvent key={item.id} hideTimestamp={item?.hideTimestamp} eventDetails={item.messageContent as string} timeStamp={item.timestamp} />
          );
        }
        if (item.type === 'Resume') {
          return item.messageContent;
        }
        return (
          <MessageComponent
            id={item.id}
            key={item.id}
            messageContent={item.messageContent}
            messageType={item.type}
            isLastMessage={isLastMessage}
            footer={{
              isVisible: isLastMessage,
              agentName: item.type !== 'user' ? item.senderDisplayName : undefined,
              timeStamp: item.timestamp,
            }}
            additionalComponents={item.additionalComponents}
            isMessageRead={item.isMessageRead}
            urlMapping={item.urlMapping}
            customMessageType={item.customMessageType}
            showFeedback={item.showFeedback}
            attachments={item.attachments}
            formatType={item.formatType}
          />
        );
      })}
    </Box>
  );
};
export default ChatWindowContent;
